import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Banknote,
  Check,
  Earth,
  Loader2,
  MapPinHouse,
  RefreshCcw,
} from "lucide-react";

import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

import { ModeSelection } from "./mode-selection";
import { LocationSearch } from "./location-search";
import { DeleteCityIntent } from "./delete-city-intent";
import { InputFieldPlaceHolder } from "../pricing/input-field-placeholder";

import { setLocation, setZoneName } from "../../redux/pricing/slice";

import { cn } from "../../utils/lib";

export function NewZoneCreationForm() {
  const dispatch = useDispatch();

  const authToken = useSelector(({ auth }) => auth.token);

  const currentSelectedCityId = useSelector(
    ({ pricing }) => pricing.currentSelectedCityId
  );

  const cityName = useSelector(
    ({ pricing }) => pricing.cities[currentSelectedCityId].cityName
  );

  const country = useSelector(
    ({ pricing }) => pricing.cities[currentSelectedCityId].country
  );

  const currency = useSelector(
    ({ pricing }) => pricing.cities[currentSelectedCityId].currency
  );

  const currentSelectedCityDistanceUnit = useSelector(
    ({ pricing }) => pricing.cities[currentSelectedCityId].distanceUnit
  );

  const { isZoneFetched } = useSelector(
    ({ pricing }) => pricing.cities[currentSelectedCityId]
  );

  const newZoneCreationForm = useSelector(
    ({ pricing }) => pricing.newZoneCreationForm
  );

  const pointA = newZoneCreationForm.pointA;
  const pointB = newZoneCreationForm.pointB;
  const zoneName = newZoneCreationForm.zoneName;
  const newZoneFormErrorMessage = newZoneCreationForm.errorMessage;
  const mode = newZoneCreationForm.mode;

  const [isSubmittingZone, setIsSubmittingZone] = React.useState(false);
  const [isError, setIsError] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);

  React.useEffect(() => {
    setTimeout(() => {
      setIsSuccess(false);
    }, 1500);
  }, [isSuccess]);

  const handleCreateZone = async () => {
    dispatch({
      type: "EXTRANET_CREATE_ZONE_BY_CITY_ID",
      payload: {
        ...newZoneCreationForm,
        bookingType: "ONEWAY",
        mode,
        currency,
        cityId: currentSelectedCityId,
        token: authToken,
        isZone: mode.trim().toLowerCase().includes("zone"),
        callbacks: {
          setIsLoading: setIsSubmittingZone,
          setIsError,
          setIsSuccess,
        },
        pricing: {},
        newCarCategory: {},
      },
    });
  };

  return (
    <div className="relative mt-0 space-y-4 text-white">
      <InputFieldPlaceHolder
        value={cityName}
        id="placeholder-city-search"
        htmlFor="placeholder-city-search"
        icon={<MapPinHouse className="size-4 text-[#B3B3B3]" />}
      />

      <InputFieldPlaceHolder
        value={country}
        id="placeholder-country-search"
        htmlFor="placeholder-country-search"
        icon={<Earth className="size-4 text-[#B3B3B3]" />}
      />

      <InputFieldPlaceHolder
        value={currency}
        id="placeholder-currency-search"
        htmlFor="placeholder-currency-search"
        icon={<Banknote className="size-4 text-[#B3B3B3]" />}
      />

      <div className="flex space-x-1 items-center">
        <div className="text-[#4E5665] font-semibold">
          Selected Distance Unit:{" "}
        </div>
        <div className="text-[#B3B3B3]">{currentSelectedCityDistanceUnit}</div>
      </div>

      <div
        className="h-px w-full border-b border-dashed border-[#181E29]"
        aria-hidden="true"
      />

      <ModeSelection />

      <div>
        <LocationSearch
          id="location-search-a"
          htmlFor="location-search-a"
          value={pointA.address}
          placeholder="Enter Point A"
          onChange={(location) => {
            dispatch(
              setLocation({
                newValue: { ...location },
                pointKey: "pointA",
                intent: "change",
              })
            );
          }}
          onSelect={(location) =>
            dispatch(
              setLocation({
                newValue: { ...location },
                pointKey: "pointA",
                intent: "set",
              })
            )
          }
        />

        <div
          className="relative left-5 h-4 w-px bg-[#4E5665]"
          aria-hidden="true"
        />

        <LocationSearch
          value={pointB.address}
          id="location-search-b"
          className="relative mb-4"
          placeholder="Enter Point B"
          htmlFor="location-search-b"
          onChange={(location) =>
            dispatch(
              setLocation({
                pointKey: "pointB",
                intent: "change",
                newValue: {
                  ...location,
                },
              })
            )
          }
          onSelect={(location) =>
            dispatch(
              setLocation({
                newValue: { ...location },
                pointKey: "pointB",
                intent: "set",
              })
            )
          }
        />

        <div className="relative">
          <Input
            placeholder="Enter Zone/Route Name"
            className={
              "focus-visible:ring-offset-0 bg-[#131821] hover:bg-[#2b3446] hover:text-white text-white border border-[#353C4A] block w-full truncate ring-1 ring-transparent placeholder:truncate focus:outline-none focus-visible:border disabled:border disabled:opacity-90 disabled:bg-[#181E29] disabled:shadow-none focus-visible:border-transparent focus-visible:ring-none focus-visible:border-black px-8"
            }
            value={zoneName}
            onChange={(e) =>
              dispatch(setZoneName({ newValue: e.target.value }))
            }
          />
          <Label
            htmlFor="city-search"
            className="absolute left-3 top-1/2 -translate-y-1/2"
          >
            <MapPinHouse className="size-4 text-white" />
          </Label>
        </div>
      </div>

      {/* Zone Form Error Messages */}
      {newZoneFormErrorMessage && (
        <p className="-translate-y-1/2 text-sm text-red-500">
          {newZoneFormErrorMessage}
        </p>
      )}

      <div className="space-y-2">
        <Button
          type="button"
          onClick={handleCreateZone}
          className="flex items-center justify-center disabled:pointer-events-auto disabled:cursor-not-allowed w-full ring-0 focus-visible:ring-0 bg-[#1d4ed8] hover:bg-[#0a44d9]"
          disabled={
            !isZoneFetched ||
            isSubmittingZone ||
            newZoneCreationForm.mode === "" ||
            !newZoneCreationForm.pointACorrect ||
            !newZoneCreationForm.pointBCorrect ||
            newZoneCreationForm.zoneName.trim() === ""
          }
        >
          {isSubmittingZone && <Loader2 className="animate-spin" />}
          {isError && <RefreshCcw className="text-red-500" />}
          {isSuccess && <Check className="text-green-500" />}
          <span>
            <span className="font-semibold">Create</span> Zone/Route
          </span>
        </Button>

        {/* Delete City Button */}
        <DeleteCityIntent />

        {/* For Styling Purpose */}
        <div className="h-8 w-full" aria-hidden="true"></div>
      </div>
    </div>
  );
}
